import _ from 'lodash'

import type {
	CompsGridDataMap,
	MeshMarginsOptions,
	NearestTopTopPusher,
	VerticalArea,
	SingleLayoutData,
} from '@wix/thunderbolt-becky-types'
import { GridPointData } from './mesh3Types'
import { bottom, top } from './mesh3Utils'
import { create as createRowsAPI, RowsAPI } from '../structure2mesh/rowsAPIFactory'

interface CompsGridDataWithRowsAPI {
	rowsAPI: RowsAPI
	compsGridData: CompsGridDataMap
}
// @ts-ignore
const getPushMargins = (compBottom, pushBottom, pushMargin, { softMargin, minLockMargin }: MeshMarginsOptions) => {
	const pushedTop = pushBottom + pushMargin
	const distance = pushedTop - compBottom
	// @ts-ignore
	return distance > minLockMargin ? softMargin : distance
}

const startNewRowIfNeeded = (rowsAPI: RowsAPI, possibleNewRowTop: number, isBottomParent: boolean) => {
	if (!isBottomParent) {
		rowsAPI.closeRow(possibleNewRowTop)
	}
}

const setPushingCompGridData = (
	compsGridData: CompsGridDataMap,
	rowsAPI: RowsAPI,
	component: SingleLayoutData,
	// @ts-ignore
	pushBottom,
	// @ts-ignore
	pushMargin,
	bottomParent: boolean,
	marginsOptions: MeshMarginsOptions
) => {
	const marginBottom = getPushMargins(bottom(component), pushBottom, pushMargin, marginsOptions)
	// @ts-ignore
	_.assign(compsGridData[component.id], { rowEnd: rowsAPI.count() + 1, bottomParent, marginBottom })
}

const splitSameRowAndSpanningComps = (
	pushingComps: Array<SingleLayoutData>,
	compsGridData: CompsGridDataMap,
	rowsAPI: RowsAPI
) => {
	const currRow = rowsAPI.count()

	return (
		_(pushingComps)
			.orderBy([top], ['desc'])
			// @ts-ignore
			.partition(({ id }) => compsGridData[id].rowStart === currRow)
			.value()
	)
}

const setSpanningCompsGridData = (
	spanningComps: Array<SingleLayoutData>,
	compsGridData: CompsGridDataMap,
	rowsAPI: RowsAPI,
	pushBottom: number,
	// @ts-ignore
	pushMargin,
	isBottomParent: boolean,
	marginsOptions: MeshMarginsOptions
) => {
	if (_.isEmpty(spanningComps)) {
		return
	}

	_.forEach(spanningComps, (component) => {
		// @ts-ignore
		rowsAPI.createExtraRows(compsGridData[component.id].rowStart, pushBottom + pushMargin)
		setPushingCompGridData(
			compsGridData,
			rowsAPI,
			component,
			pushBottom,
			pushMargin,
			isBottomParent,
			marginsOptions
		)
	})
}

const setPushingCompsGridData = (
	{ compsGridData, rowsAPI }: CompsGridDataWithRowsAPI,
	pushingComps: Array<SingleLayoutData>,
	pushBottom: number,
	// @ts-ignore
	pushMargin,
	isBottomParent: boolean,
	marginsOptions: MeshMarginsOptions
) => {
	const [sameRowComps, spanningComps] = splitSameRowAndSpanningComps(pushingComps, compsGridData, rowsAPI)

	if (!_.isEmpty(sameRowComps)) {
		_.forEach(sameRowComps, (comp) =>
			setPushingCompGridData(compsGridData, rowsAPI, comp, pushBottom, pushMargin, isBottomParent, marginsOptions)
		)
		rowsAPI.markRowWithContent()
	}
	setSpanningCompsGridData(
		spanningComps,
		compsGridData,
		rowsAPI,
		pushBottom,
		pushMargin,
		isBottomParent,
		marginsOptions
	)

	startNewRowIfNeeded(rowsAPI, pushBottom + pushMargin, isBottomParent)
}
// @ts-ignore
const getWedgeStartRow = (compsGridData, nearestTopTopPusher) =>
	nearestTopTopPusher ? compsGridData[nearestTopTopPusher].rowStart : 1

const addWedge = (
	compsGridData: CompsGridDataMap,
	rowsAPI: RowsAPI,
	compBottom: number,
	pushMargin: number,
	nearestTopTopPusher: string
) => {
	const startRow = getWedgeStartRow(compsGridData, nearestTopTopPusher)
	const rowIndex = startRow - 1
	const wedgeTop = rowsAPI.get(rowIndex)
	const wedgeBottom = compBottom + pushMargin
	const wedgeId = `wedge-${rowsAPI.count() + 1}`

	rowsAPI.createExtraRows(startRow, wedgeBottom)

	compsGridData[wedgeId] = {
		id: wedgeId,
		isWedge: true,
		height: wedgeBottom - wedgeTop,
		rowStart: startRow,
		rowEnd: rowsAPI.count() + 1,
	}

	rowsAPI.closeRow(wedgeBottom)
}

const shouldCreateWedge = (
	rowsAPI: RowsAPI,
	compBottom: number,
	pushMargin: number,
	{ minLockMargin }: MeshMarginsOptions
) => {
	// @ts-ignore
	return rowsAPI.isWedgePossible() && pushMargin > minLockMargin
}

const addWedgeIfNeeded = (
	{ compsGridData, rowsAPI }: CompsGridDataWithRowsAPI,
	y: number,
	pushMargin: number,
	nearestTopTopPusher: NearestTopTopPusher,
	pushedComps: Array<SingleLayoutData>,
	marginsOptions: MeshMarginsOptions
) => {
	if (shouldCreateWedge(rowsAPI, y, pushMargin, marginsOptions)) {
		const topTopPusher = getTopTopPusher(nearestTopTopPusher, pushedComps)
		// @ts-ignore
		addWedge(compsGridData, rowsAPI, y, pushMargin, topTopPusher)
		rowsAPI.setNoWedge()
	}
}

// @ts-ignore
const getTopTopPusher = (nearestTopTopPusher: NearestTopTopPusher, components) =>
	_.find(nearestTopTopPusher, (topTop, pushedCompId) => _.some(components, (comp) => comp.id === pushedCompId))

const setStartingCompGridData = (
	compsGridData: CompsGridDataMap,
	startingCompId: string,
	marginTop: number,
	rowStart: number
) => {
	compsGridData[startingCompId] = {
		id: startingCompId,
		marginTop,
		rowStart,
		// @ts-ignore
		rowEnd: null,
	}
}

const setStartingCompsGridData = (
	{ compsGridData, rowsAPI }: CompsGridDataWithRowsAPI,
	startingComps: Array<SingleLayoutData>
) => {
	// @ts-ignore
	const pointTop = top(_.first(startingComps))
	const marginTop = pointTop - rowsAPI.last()
	const rowStart = rowsAPI.count()
	// @ts-ignore
	_.forEach(startingComps, ({ id }) => setStartingCompGridData(compsGridData, id, marginTop, rowStart))
}

const calculateMeshCompsRows = (
	sortedGridPointsData: Array<GridPointData>,
	gridVerticalArea: VerticalArea,
	nearestTopTopPusher: NearestTopTopPusher,
	marginsOptions: MeshMarginsOptions
): CompsGridDataMap => {
	const rowsAPI = createRowsAPI(gridVerticalArea.top, {})

	const { compsGridData } = _.reduce(
		sortedGridPointsData,
		(compsGridDataWithRowsAPI, { y, pushingComps, pushMargin, startingComps }, pointIndex) => {
			const isLastPoint = pointIndex === sortedGridPointsData.length - 1

			if (pushingComps) {
				setPushingCompsGridData(
					compsGridDataWithRowsAPI,
					pushingComps,
					y,
					pushMargin,
					isLastPoint,
					marginsOptions
				)
				const pushedComps = isLastPoint ? [] : sortedGridPointsData[pointIndex + 1].startingComps
				addWedgeIfNeeded(
					compsGridDataWithRowsAPI,
					y,
					// @ts-ignore
					pushMargin,
					nearestTopTopPusher,
					pushedComps,
					marginsOptions
				)
			}

			if (startingComps) {
				setStartingCompsGridData(compsGridDataWithRowsAPI, startingComps)
				rowsAPI.setNoWedge()
			}

			return compsGridDataWithRowsAPI
		},
		{ compsGridData: {}, rowsAPI }
	)

	return compsGridData
}

export default calculateMeshCompsRows
